/**
 * ProjectDefaultRedirect Component
 *
 * Handles default page navigation for project routes.
 * - Admins: See Product page (default)
 * - Non-admins: Redirect to last visited page or V2 onboardings
 *
 * This component wraps the base project route to provide
 * role-based redirect logic.
 */

import React from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useProjectCapability } from '@modules/billing-access';
import { FUNNEL_INSIGHTS_ACCESS_CAPABILITY } from '@modules/funnel-insights/access';
import { useIntegrationSectionAccess } from '@modules/integrations/hooks/useIntegrationSectionAccess';

/** localStorage key prefix for storing last visited page per project */
const STORAGE_KEY_PREFIX = 'project-last-page-';

/** Default page for non-admins when no saved page exists */
const DEFAULT_PAGE = 'onboardings-v2';

/**
 * Valid project sub-pages that non-admins can access.
 * Used to validate saved pages from localStorage.
 */
const VALID_PAGES = [
  'onboardings',
  'onboardings-v2',
  'reports',
  'web-placements',
  'web-ab-tests',
  'stripe/products',
  'stripe/integration',
  'analytics',
  'integrations',
  'permissions',
];

interface ProjectDefaultRedirectProps {
  /** Child component to render for admins (typically ProjectViewPage) */
  children: React.ReactNode;
}

/**
 * Redirects non-admin users to the appropriate project page.
 *
 * For admins: renders children (Product page)
 * For non-admins: redirects to last visited page or V2 onboardings
 *
 * @example
 * ```tsx
 * <Route
 *   path="/project/:projectId"
 *   element={
 *     <ProjectDefaultRedirect>
 *       <ProjectViewPage />
 *     </ProjectDefaultRedirect>
 *   }
 * />
 * ```
 */
export function ProjectDefaultRedirect({ children }: ProjectDefaultRedirectProps): React.ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const auth = useAuth() as {
    isAdmin?: boolean;
    adminLoaded?: boolean;
  };
  const isAdmin = Boolean(auth.isAdmin);
  const adminLoaded = auth.adminLoaded ?? true;
  const [searchParams] = useSearchParams();
  const productAccess = useProjectCapability(projectId, 'product_analysis.access');
  const contentPlanAccess = useProjectCapability(projectId, 'content_plan.access');
  const webAccess = useProjectCapability(projectId, 'web_onboarding.access');
  const insightsAccess = useProjectCapability(projectId, FUNNEL_INSIGHTS_ACCESS_CAPABILITY);
  // Same rule as the Integrations page and its sidebar item: reachable when ≥1 section is open.
  const integrationsAccess = useIntegrationSectionAccess(projectId);

  if (!projectId) {
    return <Navigate to="/all-projects" replace />;
  }

  const loading = !adminLoaded
    || productAccess.loading
    || contentPlanAccess.loading
    || webAccess.loading
    || insightsAccess.loading
    || integrationsAccess.loading;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  // If ?tab= is present, ProductViewPage may render a product workspace tab.
  const tabParam = searchParams.get('tab');
  if (tabParam) {
    return isAdmin || productAccess.allowed
      ? <>{children}</>
      : <Navigate to="/all-projects" replace />;
  }

  // Admins see the Product page (children)
  if (isAdmin || productAccess.allowed) {
    return <>{children}</>;
  }

  // Non-admins: check for saved page in localStorage
  const storageKey = `${STORAGE_KEY_PREFIX}${projectId}`;
  const savedPage = localStorage.getItem(storageKey);

  // Validate saved page is in the list of valid pages
  const rawTargetPage = savedPage && VALID_PAGES.includes(savedPage)
    ? savedPage
    : DEFAULT_PAGE;
  const targetPage = rawTargetPage === 'onboardings' ? 'onboardings-v2' : rawTargetPage;

  const targetAllowed =
    (targetPage === 'onboardings-v2' && webAccess.allowed)
    || (targetPage === 'reports' && insightsAccess.allowed)
    || (targetPage === 'web-placements' && webAccess.allowed)
    || (targetPage === 'web-ab-tests' && webAccess.allowed)
    || (targetPage === 'stripe/products' && webAccess.allowed)
    || (targetPage === 'stripe/integration' && webAccess.allowed)
    || (targetPage === 'analytics' && webAccess.allowed)
    || (targetPage === 'integrations' && integrationsAccess.hasAny)
    || targetPage === 'permissions';

  if (targetAllowed) {
    return <Navigate to={`/project/${projectId}/${targetPage}`} replace />;
  }

  if (contentPlanAccess.allowed) {
    return <Navigate to={`/project/${projectId}/content-plan`} replace />;
  }
  if (webAccess.allowed) {
    return <Navigate to={`/project/${projectId}/onboardings-v2`} replace />;
  }

  // No capability is active (e.g. the project has no subscription). Land the
  // member on the Billing tab — which renders the paywall offer at the top with
  // a Subscribe CTA — instead of bouncing them out to /all-projects. The billing
  // route is intentionally NOT capability-gated, so it stays reachable.
  return <Navigate to={`/project/${projectId}/billing`} replace />;
}

export default ProjectDefaultRedirect;
